/**
 * Character metadata used by the arena UI.
 *
 * The canonical *interpretive* content (the eight-section .md files) lives in
 * `packages/characters/`. This file is the lightweight UI-facing version: names,
 * eras, and the topic prompts each figure is well-suited to debate.
 *
 * Roster is curated for broad recognition — figures most people have heard of
 * and can have an intuition about, spanning philosophy, strategy, and science.
 */

export interface CharacterMeta {
  id: string;
  name: string;
  era: string;
  tradition: string;
  blurb: string;
}

/**
 * Resolve the portrait URL for a character. Files live in
 * `apps/web/public/assets/portraits/<id>.jpg`.
 */
export const portraitFor = (id: string) => `/assets/portraits/${id}.jpg`;

export const CHARACTERS: CharacterMeta[] = [
  {
    id: 'aristotle',
    name: 'Aristotle',
    era: '384–322 BCE',
    tradition: 'Greek philosophy',
    blurb: 'The systematist of virtue, politics, and the natural world.',
  },
  {
    id: 'confucius',
    name: 'Confucius',
    era: '551–479 BCE',
    tradition: 'Chinese philosophy',
    blurb: 'The teacher of ritual, duty, and the well-ordered life.',
  },
  {
    id: 'einstein',
    name: 'Einstein',
    era: '1879–1955',
    tradition: 'Modern physics',
    blurb: 'The mind that unwove space and time.',
  },
  {
    id: 'machiavelli',
    name: 'Machiavelli',
    era: '1469–1527',
    tradition: 'Political philosophy',
    blurb: 'The advisor who told princes what kings would not hear.',
  },
  {
    id: 'marcus-aurelius',
    name: 'Marcus Aurelius',
    era: '121–180 CE',
    tradition: 'Roman Stoicism',
    blurb: 'The emperor-philosopher who ruled the empire and his own mind.',
  },
  {
    id: 'napoleon',
    name: 'Napoleon',
    era: '1769–1821',
    tradition: 'Imperial strategy',
    blurb: 'The emperor of will, ambition, and reform.',
  },
  {
    id: 'nietzsche',
    name: 'Nietzsche',
    era: '1844–1900',
    tradition: 'German philosophy',
    blurb: 'The hammer against morality, pity, and the herd.',
  },
  {
    id: 'plato',
    name: 'Plato',
    era: '428–348 BCE',
    tradition: 'Greek philosophy',
    blurb: 'The architect of the ideal city and the world of forms.',
  },
  {
    id: 'socrates',
    name: 'Socrates',
    era: '470–399 BCE',
    tradition: 'Greek philosophy',
    blurb: 'The questioner who knew that he knew nothing.',
  },
  {
    id: 'sun-tzu',
    name: 'Sun Tzu',
    era: '544–496 BCE',
    tradition: 'Chinese military thought',
    blurb: 'The strategist who taught that the best victory needs no fight.',
  },
];

export function findCharacter(id: string | null): CharacterMeta | null {
  if (!id) return null;
  return CHARACTERS.find((c) => c.id === id) ?? null;
}

/**
 * Topics each character is well-suited to argue. These are debate-shaped
 * prompts — phrased as questions, designed to provoke a strong position.
 */
const CHARACTER_PROMPTS: Record<string, string[]> = {
  aristotle: [
    'Is moderation always wisdom?',
    'What is the highest human good?',
    "Should the state cultivate citizens' character?",
    'Are some lives more worth living than others?',
  ],
  confucius: [
    'Should we honor tradition over progress?',
    'What does a child owe a parent?',
    'Can a ruler be just in an unjust world?',
    'Are rituals the soul of society?',
  ],
  einstein: [
    'Is the universe knowable?',
    'Can imagination outweigh knowledge?',
    'Is God a metaphor, or a fact?',
    'Should science have moral limits?',
  ],
  machiavelli: [
    'Is it better to be feared than loved?',
    'Does the end justify the means?',
    'Should a ruler ever be honest?',
    'Is morality a luxury of the powerful?',
  ],
  'marcus-aurelius': [
    'Is duty more important than desire?',
    'Should we fear death?',
    'Are foreseeable troubles still tragedies?',
    'Is what is good for the hive good for the bee?',
  ],
  napoleon: [
    'Is great history made by great men?',
    'Is power the highest political end?',
    'Can a tyrant be a liberator?',
    'Should ambition answer to morality?',
  ],
  nietzsche: [
    'Is suffering essential to greatness?',
    'Has Christianity weakened humanity?',
    'Is pity a virtue or a poison?',
    'Do universal moral truths exist?',
  ],
  plato: [
    'Should reason rule the state?',
    'Is what we see merely a shadow of the real?',
    'Should poets be exiled from the ideal city?',
    'Can the soul be perfectly just?',
  ],
  socrates: [
    'Can virtue be taught?',
    'Is the unexamined life worth living?',
    'What is justice?',
    'Is it ever right to defy the law?',
  ],
  'sun-tzu': [
    'Is victory without battle the truest victory?',
    'Should a leader hide their strength?',
    'Is deception the heart of strategy?',
  ],
};

/**
 * Pair-specific prompts. Exploit the *tension* between two specific figures —
 * topics where the disagreement is sharpest and most watchable.
 *
 * Keys are the two ids sorted alphabetically and joined with ':'.
 */
const PAIR_PROMPTS: Record<string, string[]> = {
  'aristotle:einstein': [
    'Is the natural world knowable through reason alone?',
    'Has science overtaken philosophy?',
  ],
  'aristotle:nietzsche': [
    'Is moderation a virtue — or a failure of nerve?',
    'Is happiness a worthy goal, or a sedative?',
  ],
  'aristotle:plato': [
    'Are virtues knowable in this world, or only beyond it?',
    'Is the philosopher fit to rule, or fit only to question?',
  ],
  'confucius:nietzsche': [
    'Should we revere the past, or destroy it?',
  ],
  'confucius:sun-tzu': [
    'Is order kept by virtue, or by strategy?',
  ],
  'einstein:nietzsche': [
    'Is truth discovered, or willed?',
    'Does the universe need meaning?',
  ],
  'machiavelli:marcus-aurelius': [
    'Should a ruler be virtuous, or effective?',
    'Is honesty the floor of leadership, or its ceiling?',
  ],
  'machiavelli:socrates': [
    'Is honesty a luxury, or the only ground of life?',
    'Is power its own justification?',
  ],
  'marcus-aurelius:nietzsche': [
    'Should we accept fate, or break it?',
    'Is suffering a teacher, or a wound?',
  ],
  'napoleon:socrates': [
    'Is the philosopher or the conqueror the truer mover of history?',
  ],
  'napoleon:sun-tzu': [
    'Is the greatest general the one who avoids battle?',
    'Is brilliance worthless without restraint?',
  ],
  'machiavelli:napoleon': [
    'What is power for?',
    'Should the prince obey, or be obeyed?',
  ],
  'nietzsche:plato': [
    'Should we destroy the world of forms?',
    'Is the ideal a guide, or a chain?',
  ],
  'nietzsche:socrates': [
    'Is the examined life noble — or the death of instinct?',
    'Is questioning everything wisdom, or weakness?',
  ],
  'plato:socrates': [
    'Did the student betray the teacher?',
    'Is writing philosophy a corruption of it?',
  ],
  'einstein:plato': [
    'Is mathematics a discovery, or an invention?',
  ],
  'aristotle:confucius': [
    'East and West: are the virtues the same?',
  ],
};

/** Starter prompts when no characters are selected yet. */
const STARTER_PROMPTS = [
  'What is the highest good?',
  'Is morality universal, or invented?',
  'Should power serve justice — or define it?',
  'Can virtue be taught?',
  'Is suffering necessary for meaning?',
];

/**
 * Suggested debate topics for the current selection.
 *
 * Priority:
 *   1. Pair-specific prompts (when both selected and a pair entry exists)
 *   2. Each selected character's individual prompts
 *   3. Starter prompts (when nothing selected)
 *
 * Deduplicated and capped at `max`.
 */
export function getSuggestions(
  leftId: string | null,
  rightId: string | null,
  max = 5,
): string[] {
  if (!leftId && !rightId) return STARTER_PROMPTS.slice(0, max);

  const out: string[] = [];

  if (leftId && rightId) {
    const key = [leftId, rightId].sort().join(':');
    if (PAIR_PROMPTS[key]) out.push(...PAIR_PROMPTS[key]);
  }

  if (leftId) out.push(...(CHARACTER_PROMPTS[leftId] ?? []));
  if (rightId) out.push(...(CHARACTER_PROMPTS[rightId] ?? []));

  return [...new Set(out)].slice(0, max);
}
